import { IModel } from '@alberta/konexi-shared';
import { Inject, Injectable, NgZone } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Storage } from '@ionic/storage-angular';
import { CommandResult } from 'src/app/commands/command-result';
import { ICancellationToken } from 'src/app/common/contracts/cancellation/cancellation-token';
import { ConnectionMode } from 'src/app/common/contracts/connection/connection-mode';
import { ISyncStatus } from 'src/app/common/contracts/sync/sync-status';
import { SyncStatus } from 'src/app/common/sync/sync-status';
import { EnvironmentService } from 'src/app/shared/services/environment/environment.service';

import { AuthService } from './auth.service';
import { ConnectionStateService } from './connection-state.service';
import { IAdditionalSyncData } from './contracts/sync/additional-sync-data';
import { IClientDataSyncService } from './contracts/sync/client-data-sync';
import { IConnectionStateService } from './contracts/sync/connection-state-service';
import { IFeathersService } from './contracts/sync/feathers-service';
import { IFullSyncService } from './contracts/sync/service/full-sync-service';
import { ISyncService } from './contracts/sync/service/sync-service';
import { SyncProgressEvent } from '../models/sync-progress-event';
import { EventService } from './event.service';
import { FeathersService } from './feathers.service';
import { SyncTimeoutRepeater } from './sync/sync-timeout-repeater.service';
import { SyncService } from './sync/sync.service';
import { TrackerService } from './tracker.service';

@Injectable({
  providedIn: 'root',
})
export class ClientDataSyncService implements IClientDataSyncService {
  constructor(
    private _storage: Storage,
    @Inject(SyncService) private _syncServices: ISyncService[],
    @Inject(ConnectionStateService) private _connectionStateService: IConnectionStateService,
    @Inject(FeathersService) private _feathersService: IFeathersService,
    @Inject(SyncStatus) private _syncStatus: ISyncStatus,
    private _authService: AuthService,
    private _ngZone: NgZone,
    private _environmnetService: EnvironmentService,
    private _tracker: TrackerService,
    private _eventService: EventService<SyncProgressEvent>,
    private _platform: Platform
  ) {
    const copyOfSyncServices = [..._syncServices];
    this._syncServices.length = 0;
    for (const service of copyOfSyncServices) {
      this._syncServices.push(...service.resolve());
    }

    this._feathersService.connectionModeChange.subscribe(connectionMode => {
      if (connectionMode === ConnectionMode.default) {
        this._syncServices.forEach(async service => await service.setup(this._feathersService));
      } else {
        this._syncServices.forEach(service => service.removeAllListeners(connectionMode));
      }
    });
  }

  public async countDocuments(serviceName: string): Promise<number> {
    const service = this._syncServices.find(syncService => syncService.name === serviceName);
    if (!service) {
      return 0;
    }

    await service.setup(this._feathersService);
    const account = await this._authService.getAccount();
    return service.countDocuments(account);
  }

  public async startSync(token: ICancellationToken): Promise<void> {
    for (const service of this._syncServices) {
      await service.setup(this._feathersService);
    }
    const promise = this.sync(token);

    token.promise = promise;

    return promise;
  }

  public async write(
    channel: string = '',
    payload: IModel = { _id: '', archived: false, timestamp: new Date() },
    additionalData: IAdditionalSyncData
  ): Promise<CommandResult> {
    if (!this._connectionStateService.isConnected) {
      return new CommandResult({ successful: false });
    }

    for (const service of this._syncServices) {
      if (this.isFullSyncService(service) && service.canSync(channel)) {
        await service.setup(this._feathersService);
        const result = await service.write(payload, additionalData, channel, await this._authService.getAccount());

        return new CommandResult({ successful: true, result });
      }
    }
    return new CommandResult({ successful: false });
  }

  private async sync(token: ICancellationToken): Promise<any> {
    this._syncStatus.completedData = false;
    this._syncStatus.completedSync = false;
    token.syncTerminal = {};
    const userId = await this._authService.getCurrentUserId();
    const userSync = await this._storage.get(`sync_${this._environmnetService.branch}_${userId}`);

    // Close the sync indicator as soon as EVERY service is terminal (completed, errored or
    // cancelled) - not on the first error (which would close it while other services still sync)
    // and not only after all repeaters settle (an unreachable service retries for minutes, work
    // item 15660). Runs concurrently with the sync; the modal close is decoupled from sync().
    const terminalWatcher = this.watchAllServicesTerminal(token);

    await Promise.all(
      this._syncServices.map(async syncService =>
        new SyncTimeoutRepeater(syncService, this._ngZone, userSync)
          .sync(token, await this._authService.getAccount())
          .catch(() => undefined)
      )
    );

    await this._storage.set(`sync_${this._environmnetService.branch}_${userId}`, await this.userSyncToSave(userSync));

    return new Promise<any>(resolve =>
      this.waitUntilSyncCompleted(token, () => {
        if (terminalWatcher) {
          global.clearInterval(terminalWatcher);
        }
        resolve(undefined);
      })
    );
  }

  /**
   * Polls until every sync service has reached a terminal state (completed or errored/cancelled -
   * tracked via token.syncTerminal by SyncTimeoutRepeater) and then signals the SyncIndicatorPage
   * that it may close. A single failing service therefore no longer closes the indicator, while an
   * unreachable service (which is terminal as soon as it errors) cannot keep it open indefinitely.
   */
  private watchAllServicesTerminal(token: ICancellationToken): ReturnType<typeof setInterval> | undefined {
    // Only drives the mobile sync indicator. On web the (separate) WebDataSyncService runs and the
    // indicator is closed by the sync pipeline; guarding here avoids a premature close there.
    if (!this._platform.is('hybrid') || !this._syncServices.length) {
      return undefined;
    }
    let dispatched = false;
    const interval = this._ngZone.runOutsideAngular(() =>
      global.setInterval(() => {
        const allTerminal = this._syncServices.every(service => token.syncTerminal?.[service.name] === true);
        if (allTerminal && !dispatched) {
          dispatched = true;
          global.clearInterval(interval);
          const failed = this._syncServices
            .filter(service => token.syncCompleted?.[service.name] !== true)
            .map(service => service.name);
          window.logger.info(
            `[sync] all ${this._syncServices.length} services terminal; ` +
              `failed/incomplete: ${failed.length ? failed.join(', ') : 'none'} - closing sync indicator`
          );
          // Emit the "all services terminal" signal (isAllTerminal = the 5th arg). SyncIndicatorPage
          // subscribes to this same EventService and, on an isAllTerminal event, dismisses the sync
          // indicator modal. This is a control signal, not a progress update, so title is '' and
          // total/progress are -1 sentinels (there is no single service it belongs to).
          this._eventService.dispatchEvent(new SyncProgressEvent('', -1, -1, false, true));
        }
      }, 500)
    );
    return interval;
  }

  private async userSyncToSave(runningUserSync) {
    const userId = await this._authService.getCurrentUserId();
    const currentUserSync = await this._storage.get(`sync_${this._environmnetService.branch}_${userId}`);

    return (!runningUserSync && currentUserSync) ||
      (runningUserSync && currentUserSync && runningUserSync.version !== currentUserSync.version)
      ? currentUserSync.status.push(...(runningUserSync || { status: [] }).status) && currentUserSync
      : runningUserSync;
  }

  private waitUntilSyncCompleted(token: ICancellationToken, resolve: (value?: {} | PromiseLike<{}>) => void) {
    this._ngZone.runOutsideAngular(() => {
      const interval = global.setInterval(async () => {
        if (!Object.keys(token.serviceRepeats).length) {
          this._syncStatus.completedData = Object.values(token.syncCompleted || {}).every(value => value);
          this._syncStatus.completedSync = true;
          resolve();
          global.clearInterval(interval);
          this._tracker.trackSyncFinished();
          return;
        }
      }, 500);
    });
  }

  private isFullSyncService(syncService: any): syncService is IFullSyncService {
    return syncService && syncService.write && syncService.canSync;
  }
}
